export interface AudioCapture {
  stop: () => void;
}

export async function startCapture(
  send: (buf: ArrayBuffer) => void,
): Promise<AudioCapture> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, sampleRate: 16000 },
  });
  const ctx = new AudioContext({ sampleRate: 16000 });
  const source = ctx.createMediaStreamSource(stream);
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  source.connect(processor);
  processor.connect(ctx.destination);

  processor.onaudioprocess = (e) => {
    const input = e.inputBuffer.getChannelData(0);
    const int16 = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    send(int16.buffer);
  };

  return {
    stop() {
      try {
        processor.disconnect();
        source.disconnect();
      } catch { /* */ }
      stream.getTracks().forEach((t) => t.stop());
      ctx.close().catch(() => {});
    },
  };
}

export class AudioQueue {
  private queue: string[] = [];
  private el = new Audio();
  private playing = false;

  constructor() {
    // Prime audio playback after first user gesture
    this.el.autoplay = true;
    this.el.src =
      "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
  }

  enqueue(base64Mp3: string) {
    this.queue.push(base64Mp3);
    this.playNext();
  }

  private playNext() {
    if (this.playing || this.queue.length === 0) return;
    this.playing = true;
    const data = this.queue.shift()!;

    this.el.oncanplay = () => {
      this.el.play().catch((err) => {
        console.error("audio play:", err);
        this.playing = false;
        this.playNext();
      });
    };
    this.el.onended = () => {
      this.playing = false;
      this.playNext();
    };
    this.el.onerror = () => {
      this.playing = false;
      this.playNext();
    };
    this.el.src = `data:audio/mp3;base64,${data}`;
  }
}
